import React from 'react';
import { User, Download } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ReasoningPanel from './ReasoningPanel';
import { VisualizationRenderer } from './Chat/visualizations/VisualizationRenderer';
import DemoHighlightBadge from './DemoHighlightBadge';

interface ChatMessageProps {
  message: {
    role: 'user' | 'assistant';
    content: string;
    agentName?: string;
    timestamp: Date;
    data?: any;
    visualization?: any; // Updated to accept any visualization type
    reasoning?: any;
    show_reasoning?: boolean;
  };
  onAction?: (action: string, params?: any) => void;
  highlightText?: string;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, onAction, highlightText }) => {
  const isUser = message.role === 'user';

  // Debug logging for assistant messages
  if (!isUser) {
    console.log('💬 [ChatMessage] Rendering assistant message:', {
      hasVisualization: !!message.visualization,
      hasDataVisualization: !!message.data?.visualization,
      visualizationType: message.visualization?.type || message.data?.visualization?.type,
      messageId: message.timestamp
    });
  }

  // Check if download data is available
  const hasDownloadData = message.data?.metadata?.download_available &&
                          message.data?.metadata?.all_significant_results;

  // Function to download results as CSV
  const handleDownload = () => {
    if (!hasDownloadData) return;

    const results = message.data.metadata.all_significant_results;
    const disease = message.data.metadata.disease || 'disease';
    const cellType = message.data.metadata.cell_type || 'celltype';

    // Create CSV content
    const csvHeader = 'Target,Rank\n';
    const csvRows = results.map((r: any) =>
      `${r.target},${r.rank ?? '-'}`
    ).join('\n');
    const csvContent = csvHeader + csvRows;

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${disease}_${cellType}_targets.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`flex items-start gap-2 md:gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      {isUser ? (
        <div className="p-2 rounded-lg flex-shrink-0 bg-gradient-to-br from-blue-500 to-purple-500">
          <User className="w-4 h-4 text-white" />
        </div>
      ) : (
        <div className="rounded-lg flex-shrink-0 overflow-hidden">
          <img src="/LogoFinal.png?v=5" alt="RNAverse" className="w-8 h-8" />
        </div>
      )}

      {/* Message Content */}
      <div className={`flex-1 min-w-0 ${isUser ? 'flex justify-end' : ''}`}>
        <div
          className={`rounded-2xl ${
            isUser
              ? 'max-w-2xl bg-gray-800 rounded-tr-sm px-4 py-3'
              : 'w-full px-1 py-1'
          }`}
        >
          {/* Message Content */}
          <div className={`prose prose-sm max-w-none ${isUser ? '' : 'px-3'}`}>
            {isUser ? (
              <p className="text-gray-100 whitespace-pre-wrap m-0 text-sm">{message.content}</p>
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code: ({ node, className, children, ...props }: any) => {
                    const inline = !className?.includes('language-');
                    return inline ? (
                      <code className="bg-gray-800 text-gray-300 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                        {children}
                      </code>
                    ) : (
                      <code className="block bg-gray-900 border border-gray-700 text-gray-300 p-3 rounded-lg overflow-x-auto text-sm font-mono" {...props}>
                        {children}
                      </code>
                    );
                  },
                  a: ({ node, ...props }) => (
                    <a className="text-blue-400 hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer" {...props} />
                  ),
                  ul: ({ node, ...props }) => (
                    <ul className="list-disc list-inside space-y-1 text-gray-300" {...props} />
                  ),
                  ol: ({ node, ...props }) => (
                    <ol className="list-decimal list-inside space-y-1 text-gray-300" {...props} />
                  ),
                  h1: ({ node, ...props }) => (
                    <h1 className="text-xl font-bold text-gray-100 mt-4 mb-2" {...props} />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2 className="text-lg font-bold text-gray-100 mt-3 mb-2" {...props} />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3 className="text-base font-semibold text-gray-100 mt-2 mb-1" {...props} />
                  ),
                  p: ({ node, ...props }) => (
                    <p className="text-gray-200 mb-2 leading-relaxed" {...props} />
                  ),
                  strong: ({ node, ...props }) => (
                    <strong className="text-gray-100 font-semibold" {...props} />
                  ),
                  table: ({ node, ...props }) => (
                    <div className="overflow-x-auto my-4">
                      <table className="min-w-full border border-gray-700 rounded-lg" {...props} />
                    </div>
                  ),
                  th: ({ node, ...props }) => (
                    <th className="border border-gray-700 bg-gray-800 px-3 py-2 text-left font-semibold text-gray-300 text-sm" {...props} />
                  ),
                  td: ({ node, ...props }) => (
                    <td className="border border-gray-700 px-3 py-2 text-gray-300 text-sm" {...props} />
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            )}
          </div>

          {/* Download Button */}
          {hasDownloadData && (
            <div className="mt-3 px-3">
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all duration-200 font-medium text-sm shadow-sm"
              >
                <Download className="w-4 h-4" />
                Download All Results (CSV)
              </button>
            </div>
          )}

          {/* Reasoning Panel */}
          {!isUser && message.reasoning && message.show_reasoning && (
            <div className="mt-3 px-3">
              <ReasoningPanel reasoning={message.reasoning} />
            </div>
          )}

          {/* Visualizations */}
          {!isUser && (message.visualization || message.data?.visualization) && (() => {
            const viz = message.visualization || message.data?.visualization;
            const vizArray = Array.isArray(viz) ? viz : [viz];
            return vizArray.map((v, idx) => (
              <div key={idx} className="relative mt-3 bg-gray-900 border border-gray-700 rounded-xl overflow-visible">
                <VisualizationRenderer visualization={v} onAction={onAction} />
                {highlightText && idx === 0 && (
                  <DemoHighlightBadge text={highlightText} />
                )}
              </div>
            ));
          })()}

          {/* Timestamp */}
          <div className={`mt-2 text-xs text-gray-400 ${isUser ? 'text-right' : 'px-3'}`}>
            {message.timestamp instanceof Date && !isNaN(message.timestamp.getTime())
              ? message.timestamp.toLocaleTimeString()
              : 'Just now'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
